import useTranslation from 'next-translate/useTranslation'

import useAppSelector from '../../../../store/hooks/useAppSelector'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import {
  checkoutCapacityConflictAllocationSelector,
  checkoutCapacityConflictRequestedTimeSelector,
  checkoutCapacityConflictTypeSelector,
  checkoutCapacityConflictOptionsSelector,
} from '../../store/checkoutSelectors'
import { checkoutSetCapacityConflictSelectedOption } from '../../store/checkoutSlice'
import {
  CapacityConflictAdjustVerb,
  CapacityConflictOptionType,
  CapacityConflictType,
} from '../../types'
import getCapacityConflictRequestedTimeLabel from '../../utils/getCapacityConflictRequestedTimeLabel'
import useCapacityConflictActions from '../../hooks/useCapacityConflictActions'

import CapacityConflictOptionRow from './CapacityConflictOptionRow'
import CapacityConflictFooter from './CapacityConflictFooter'
import CapacityConflictStepDescription from './CapacityConflictStepDescription'
import styles from './CapacityConflictChoiceStep.module.scss'

export default function CapacityConflictChoiceStep() {
  const { t } = useTranslation('common')

  const dispatch = useAppDispatch()
  const { cancel } = useCapacityConflictActions()

  const conflictType = useAppSelector(checkoutCapacityConflictTypeSelector)
  const options = useAppSelector(checkoutCapacityConflictOptionsSelector)
  const allocation = useAppSelector(checkoutCapacityConflictAllocationSelector)
  const requestedTime = useAppSelector(
    checkoutCapacityConflictRequestedTimeSelector,
  )

  if (conflictType === undefined || !allocation) {
    return null
  }

  const { verb, hasContestedPools, removeCount } = allocation
  const formattedRequestedTime = getCapacityConflictRequestedTimeLabel(
    requestedTime,
    t,
  )

  function handleSelect(key: CapacityConflictOptionType) {
    dispatch(checkoutSetCapacityConflictSelectedOption(key))
  }

  function handleCancel() {
    cancel()
  }

  function getCapacityConflictBaseMessage(type: CapacityConflictType): string {
    return type === CapacityConflictType.AllProductsBlocked
      ? t('checkoutCapacityConflict.baseMessageAllBlockedWithTimes', {
          time: formattedRequestedTime,
        })
      : t('checkoutCapacityConflict.baseMessageSomeBlockedWithTimes', {
          time: formattedRequestedTime,
        })
  }

  function getOptionTitle(key: CapacityConflictOptionType): string {
    if (
      key === CapacityConflictOptionType.AdjustOrder &&
      verb === CapacityConflictAdjustVerb.Remove
    ) {
      return t('checkoutCapacityConflict.optionLabel.removeBlocking')
    }

    return t(`checkoutCapacityConflict.optionLabel.${key}`)
  }

  /**
   * The adjust option says what it will actually do - hand the guest the choice or
   * remove - so it never promises a removal that isn't coming.
   */
  function getAdjustOptionSubtitle(): string {
    return hasContestedPools
      ? t('checkoutCapacityConflict.optionMessage.adjustOrderChoose')
      : t('checkoutCapacityConflict.optionMessage.adjustOrderRemove', {
          count: removeCount,
        })
  }

  function getOptionSubtitle(key: CapacityConflictOptionType): string {
    return key === CapacityConflictOptionType.AdjustOrder
      ? getAdjustOptionSubtitle()
      : t(`checkoutCapacityConflict.optionMessage.${key}`)
  }

  return (
    <>
      <CapacityConflictStepDescription>
        {getCapacityConflictBaseMessage(conflictType)}
      </CapacityConflictStepDescription>
      <div className={styles.choiceGroup}>
        {options.map(key => (
          <CapacityConflictOptionRow
            key={key}
            variant={key}
            title={getOptionTitle(key)}
            subtitle={getOptionSubtitle(key)}
            onClick={() => handleSelect(key)}
          />
        ))}
      </div>
      <CapacityConflictFooter
        secondaryLabel={t('checkoutCapacityConflict.cancelButton')}
        onSecondaryClick={handleCancel}
      />
    </>
  )
}
